"use client";

import { useEffect, useRef, useState } from "react";

interface Question {
  id: string;
  text: string;
  category: string;
}

const questions: Question[] = [
  {
    id: "q1",
    text: "Chaque logement dispose d'un Process Book ou d'une fiche standardisée accessible à votre équipe.",
    category: "Standardisation",
  },
  {
    id: "q2",
    text: "Toutes les informations opérationnelles (codes, équipements, contacts) sont centralisées dans un outil unique.",
    category: "Centralisation",
  },
  {
    id: "q3",
    text: "Vous envoyez automatiquement des messages aux voyageurs avant et après leur séjour.",
    category: "Automatisation",
  },
  {
    id: "q4",
    text: "Vous suivez mensuellement votre taux d'avis 5★ et votre temps de réponse moyen.",
    category: "Pilotage qualité",
  },
  {
    id: "q5",
    text: "Intégrer un nouveau prestataire ou collaborateur prend moins de 2 heures grâce à vos process documentés.",
    category: "Scalabilité",
  },
];

function getResult(score: number) {
  if (score <= 1) {
    return {
      label: "Phase artisanale",
      emoji: "🔴",
      color: "#DC2626",
      bg: "rgba(220,38,38,0.06)",
      border: "rgba(220,38,38,0.2)",
      message:
        "Votre conciergerie repose encore sur l'improvisation. Chaque croissance supplémentaire amplifie les risques. Il est temps de poser les fondations avec les 4 piliers.",
      cta: "Commencer par le Pilier 1 : Standardisation",
    };
  } else if (score <= 3) {
    return {
      label: "En transition",
      emoji: "🟡",
      color: "#C9A84C",
      bg: "rgba(201,168,76,0.06)",
      border: "rgba(201,168,76,0.2)",
      message:
        "Vous avez commencé à structurer, mais des piliers manquent encore. Identifiez les cases non cochées ci-dessus et priorisez-les dans les 30 prochains jours.",
      cta: "Identifier mes piliers manquants",
    };
  } else {
    return {
      label: "Conciergerie structurée",
      emoji: "🟢",
      color: "#4A6741",
      bg: "rgba(74,103,65,0.06)",
      border: "rgba(74,103,65,0.2)",
      message:
        "Félicitations ! Vous avez une base solide. L'étape suivante est l'industrialisation complète : automatiser les 3 derniers points non cochés et viser le Niveau 3 de la matrice.",
      cta: "Passer au Niveau 3 avec GîteZen Pro",
    };
  }
}

export default function DiagnosticSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleAnswer = (id: string) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const score = Object.values(answers).filter(Boolean).length;
  const result = getResult(score);

  const scrollToSolution = () => {
    document.getElementById("solution")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="diagnostic"
      ref={sectionRef}
      className="py-28 md:py-40 px-6 md:px-16"
      style={{ background: "var(--cream)" }}
    >
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div
          className={`flex items-center gap-6 mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="font-serif italic text-sm" style={{ color: "var(--sage)" }}>
            Page 09
          </span>
          <div className="gold-line" />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--warm-gray)" }}>
            Auto-diagnostic
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left */}
          <div className="lg:col-span-4">
            <h2
              className={`font-serif italic leading-tight mb-6 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", color: "var(--charcoal)" }}
            >
              Évaluez votre niveau{" "}
              <span className="not-italic shimmer-text">en 2 minutes.</span>
            </h2>
            <p
              className={`text-sm leading-relaxed mb-8 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ color: "var(--warm-gray)" }}
            >
              Cochez les affirmations vraies pour votre conciergerie. Soyez honnête — ce diagnostic est pour vous, pas pour les autres.
            </p>

            {/* Score display */}
            <div
              className={`rounded-2xl p-8 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ background: "var(--ivory)", border: "1px solid var(--border)" }}
            >
              <div className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "var(--warm-gray)" }}>
                Votre score
              </div>
              <div className="flex items-end gap-2 mb-4">
                <span
                  className="font-serif text-6xl font-bold"
                  style={{ color: "var(--charcoal)" }}
                >
                  {score}
                </span>
                <span className="font-serif text-2xl mb-3" style={{ color: "var(--warm-gray)" }}>
                  / 5
                </span>
              </div>

              {/* Score bar */}
              <div
                className="h-3 rounded-full mb-4"
                style={{ background: "rgba(28,25,23,0.08)" }}
              >
                <div
                  className="h-3 rounded-full transition-all duration-700"
                  style={{
                    width: `${(score / 5) * 100}%`,
                    background: `linear-gradient(90deg, var(--sage), var(--gold))`,
                  }}
                />
              </div>

              {score > 0 && (
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-lg">{result.emoji}</span>
                  <span className="text-sm font-bold" style={{ color: result.color }}>
                    {result.label}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Questions */}
          <div className="lg:col-span-8">
            <div className="space-y-4">
              {questions.map((q, i) => (
                <button
                  key={q.id}
                  onClick={() => toggleAnswer(q.id)}
                  className={`diag-option w-full text-left rounded-xl p-6 transition-all duration-300 ${
                    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  } ${answers[q.id] ? "selected" : ""}`}
                  style={{ transitionDelay: `${0.2 + i * 0.1}s` }}
                  aria-pressed={answers[q.id] || false}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`diag-check flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        answers[q.id] ? "selected" : ""
                      }`}
                    >
                      {answers[q.id] && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div
                        className="text-xs font-bold uppercase tracking-widest mb-2"
                        style={{ color: "var(--sage)" }}
                      >
                        {q.category}
                      </div>
                      <p className="text-sm leading-relaxed" style={{ color: "var(--charcoal)" }}>
                        {q.text}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Result card */}
            {score > 0 && (
              <div
                className="mt-10 rounded-2xl p-8"
                style={{ background: result.bg, border: `1px solid ${result.border}` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{result.emoji}</span>
                  <span
                    className="font-serif italic text-xl"
                    style={{ color: "var(--charcoal)" }}
                  >
                    {result.label}
                  </span>
                  <span
                    className="ml-auto text-xs font-bold px-3 py-1 rounded-full"
                    style={{ background: result.color, color: "white" }}
                  >
                    {score}/5
                  </span>
                </div>
                <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--warm-gray)" }}>
                  {result.message}
                </p>
                <button
                  onClick={scrollToSolution}
                  className="btn-primary text-xs"
                  style={{ background: result.color }}
                >
                  {result.cta} →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}